// filepath: apps/web/src/features/promote/usePromotionReview.ts
//
// FR-UI-PROM-1: TanStack Query hook for fetching the promotion review context.
// GET /api/admin/promotion-reviews/{id}/context
//
// The response includes the review row state and the re-hydrated diff_report
// (DefinitionDiff[]) from the metadata stored at submit-for-review time.

import { useQuery } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import type { ReviewContext } from "./types";

export const promoteKeys = {
  reviewContext: (tenantSlug: string, reviewId: string) =>
    ["promote", tenantSlug, "reviewContext", reviewId] as const,
};

/**
 * Fetches the promotion review context for the reviewer UI.
 * The diff_report is re-hydrated from the stored PromotionPlan metadata.
 */
export function usePromotionReview(reviewId: string) {
  const { tenantSlug } = useTenant();
  return useQuery({
    queryKey: promoteKeys.reviewContext(tenantSlug, reviewId),
    queryFn: () =>
      apiFetch(`/api/admin/promotion-reviews/${reviewId}/context`).then((r) => {
        if (!r.ok) {
          throw new ApiError(r.statusText, r.status, `http_${r.status}`);
        }
        return r.json() as Promise<ReviewContext>;
      }),
    enabled: Boolean(reviewId),
  });
}
